import { err, ok, type Result } from '@src/services/common/Result';
import { extractErrorInfo, isAbortError } from '@src/utility/error';
import { LogManager } from '@src/utility/Logger';

const logger = LogManager.getLogger('transcript:TopicTagging');

export interface TopicTaggingInputTopic {
  title: string;
  speakerLabels?: string[];
}

export interface TopicTaggingInputParagraph {
  paragraphId: string;
  speakerLabel?: string;
  text: string;
}

export interface TopicTaggingResult {
  byTopic: Record<string, string[]>;
}

export interface TopicTaggingInput {
  paragraphs: TopicTaggingInputParagraph[];
  topics: TopicTaggingInputTopic[];
  storyId: string;
  storyName?: string;
  userEmail?: string;
  userId?: string;
  model?: string;
  signal?: AbortSignal;
}

const MAX_PARAGRAPHS_PER_CHUNK = 150;

/**
 * Stateless service that uses an LLM to assign transcript paragraphs to story
 * topic titles. Returns `byTopic: { [topicTitle]: paragraph_id[] }`.
 *
 * Calls the existing /api/aiprompts/regenerate-gpt-response-stream endpoint
 * directly with a constructed systemMessage (no DatoCMS AI prompt template
 * needed). Mirrors the request shape used by stream-talking-point-response in
 * ChatGPTService, which is the precedent for code-side systemMessage paths.
 */
export class TopicTaggingService {
  constructor(private readonly apiUrl: string) {}

  async tagParagraphs(
    input: TopicTaggingInput,
  ): Promise<Result<TopicTaggingResult>> {
    if (!input.paragraphs.length) {
      return ok({ byTopic: {} });
    }
    if (!input.topics.length) {
      return ok({ byTopic: {} });
    }

    const chunks = chunkParagraphs(input.paragraphs, MAX_PARAGRAPHS_PER_CHUNK);
    const validTopicTitles = new Set(input.topics.map(t => t.title));

    const byTopic: Record<string, string[]> = {};
    const seenByTopic: Record<string, Set<string>> = {};
    for (const topic of input.topics) {
      byTopic[topic.title] = [];
      seenByTopic[topic.title] = new Set();
    }

    for (const chunk of chunks) {
      if (input.signal?.aborted) {
        return err('EXTERNAL', 'Topic tagging aborted before chunk');
      }
      const chunkResult = await this.tagChunk(input, chunk);
      if (!chunkResult.ok) return chunkResult;

      // Validate against THIS chunk's paragraph_ids only. The LLM only saw
      // these in its prompt — a paragraph_id from a different chunk would be
      // a hallucination, even if it happens to exist elsewhere in the story.
      const chunkParagraphIds = new Set(chunk.map(p => p.paragraphId));

      for (const [topic, paragraphIds] of Object.entries(
        chunkResult.value.byTopic,
      )) {
        if (!validTopicTitles.has(topic)) {
          logger.warn('Dropping unknown topic from AI response', { topic });
          continue;
        }
        const seen = seenByTopic[topic];
        for (const pid of paragraphIds) {
          if (!chunkParagraphIds.has(pid)) {
            logger.warn('Dropping unknown paragraph_id from AI response', {
              topic,
              pid,
            });
            continue;
          }
          if (seen.has(pid)) continue;
          seen.add(pid);
          byTopic[topic].push(pid);
        }
      }
    }

    return ok({ byTopic });
  }

  private async tagChunk(
    input: TopicTaggingInput,
    paragraphs: TopicTaggingInputParagraph[],
  ): Promise<Result<TopicTaggingResult>> {
    const systemMessage = buildSystemMessage(input.topics);
    const transcript = buildTranscriptPayload(paragraphs);

    let res: Response;
    try {
      res = await fetch(
        `${this.apiUrl}/api/aiprompts/regenerate-gpt-response-stream`,
        {
          signal: input.signal,
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            systemMessage,
            transcript,
            storyId: input.storyId,
            story_name: input.storyName,
            user_email: input.userEmail,
            user_id: input.userId,
            model: input.model,
            prompt_title: 'Transcript | Topic Tagging',
            prompt_type: 'SYSTEM',
            useSearch: false,
          }),
        },
      );
    } catch (e) {
      if (isAbortError(e)) {
        return err('EXTERNAL', 'Topic tagging aborted');
      }
      const info = extractErrorInfo(e);
      logger.error('Topic tagging fetch failed', info);
      return err('EXTERNAL', 'Network error while tagging topics', info);
    }

    if (!res.ok) {
      const info = { status: res.status, statusText: res.statusText };
      logger.error('Topic tagging endpoint returned non-2xx', info);
      return err('EXTERNAL', 'Topic tagging request failed', info);
    }

    const body = res.body;
    if (!body) {
      return err('EXTERNAL', 'Topic tagging response had no body');
    }

    const reader = body.getReader();
    // `stream: true` keeps the decoder's internal state across chunks so a
    // multi-byte UTF-8 sequence split across reads decodes correctly; a
    // final `decoder.decode()` flushes any trailing bytes after the loop.
    const decoder = new TextDecoder();
    let raw = '';
    try {
      let done = false;
      while (!done) {
        if (input.signal?.aborted) {
          await reader.cancel().catch(() => undefined);
          return err('EXTERNAL', 'Topic tagging aborted');
        }
        const next = await reader.read();
        done = next.done;
        if (next.value) raw += decoder.decode(next.value, { stream: true });
      }
      raw += decoder.decode();
    } catch (e) {
      if (isAbortError(e)) {
        return err('EXTERNAL', 'Topic tagging aborted');
      }
      const info = extractErrorInfo(e);
      logger.error('Topic tagging stream read failed', info);
      return err('EXTERNAL', 'Failed reading topic tagging stream', info);
    }

    const parsed = parseTopicAssignments(raw);
    if (!parsed) {
      logger.error('Topic tagging response unparseable', { raw });
      return err('EXTERNAL', 'Could not parse topic tagging response', { raw });
    }
    return ok({ byTopic: parsed });
  }
}

function chunkParagraphs(
  paragraphs: TopicTaggingInputParagraph[],
  size: number,
): TopicTaggingInputParagraph[][] {
  if (paragraphs.length <= size) return [paragraphs];
  const chunks: TopicTaggingInputParagraph[][] = [];
  for (let i = 0; i < paragraphs.length; i += size) {
    chunks.push(paragraphs.slice(i, i + size));
  }
  return chunks;
}

function buildSystemMessage(topics: TopicTaggingInputTopic[]): string {
  const topicLines = topics
    .map(t => {
      const speakerHint =
        t.speakerLabels && t.speakerLabels.length
          ? ` (only paragraphs spoken by: ${t.speakerLabels.join(', ')})`
          : '';
      return `- "${t.title}"${speakerHint}`;
    })
    .join('\n');

  return [
    'You categorize transcript paragraphs by topic.',
    'You will receive a list of topics and a list of transcript paragraphs.',
    'Each paragraph has a unique id and may have a speaker label.',
    '',
    'Assign zero or more topics to each paragraph based on subject matter.',
    'If a topic restricts itself to specific speaker labels, only assign that topic',
    'to paragraphs whose speaker matches one of those labels.',
    '',
    `Topics:\n${topicLines}`,
    '',
    'Respond ONLY with a JSON object in this exact shape, no commentary, no markdown:',
    '{',
    '  "byTopic": {',
    '    "<topic title>": ["<paragraph_id>", ...],',
    '    ...',
    '  }',
    '}',
    '',
    'Use the exact topic titles given above as keys. Use only paragraph ids',
    'that appear in the input. If a topic has no matching paragraphs, return',
    'an empty array for it.',
  ].join('\n');
}

function buildTranscriptPayload(
  paragraphs: TopicTaggingInputParagraph[],
): string {
  return paragraphs
    .map(p => {
      const speaker = p.speakerLabel ? ` speaker=${p.speakerLabel}` : '';
      return `[${p.paragraphId}${speaker}] ${p.text}`;
    })
    .join('\n\n');
}

/**
 * Robust JSON-from-stream parser modeled on ChatGPTService.identifySpeakersInChunk.
 * Layers: direct JSON.parse → markdown-fence strip → object-regex extraction.
 * Returns `byTopic` shape on success, null on failure.
 */
function parseTopicAssignments(raw: string): Record<string, string[]> | null {
  let cleaned = raw.trim();
  cleaned = cleaned.replace(/^```(?:json)?\s*/i, '');
  cleaned = cleaned.replace(/\s*```\s*$/i, '');
  cleaned = cleaned.trim();

  const tryParse = (s: string): unknown => {
    try {
      return JSON.parse(s);
    } catch {
      return undefined;
    }
  };

  let parsed: unknown = tryParse(cleaned);
  if (parsed === undefined) {
    const match = cleaned.match(/\{[\s\S]*\}/);
    if (match) parsed = tryParse(match[0]);
  }
  if (!parsed || typeof parsed !== 'object') return null;

  const obj = parsed as { byTopic?: unknown } & Record<string, unknown>;
  const candidate = obj.byTopic ?? obj;
  if (!candidate || typeof candidate !== 'object') return null;

  const result: Record<string, string[]> = {};
  for (const [topic, value] of Object.entries(
    candidate as Record<string, unknown>,
  )) {
    if (!Array.isArray(value)) continue;
    result[topic] = value.filter(
      (v): v is string => typeof v === 'string' && v.length > 0,
    );
  }
  return result;
}
